import type { ExpertWorkspaceWebSocket } from "@expert/websocket";

import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { getAmazonConnectLoginPopupConfig } from "@expert/amazon-connect-utils";
import { getAppConfigSingleton } from "@expert/app-config";
import { sendMetrics } from "@expert/cloudwatch-metrics";
import { getLogger } from "@expert/logging";
import { setFullStoryUserProperties } from "@expert/monitoring";
import { getAmazonConnectLoginUrl, type ServiceProvider, selectPartnerWithOverride } from "@expert/service-providers";

import type { ChatEventCallbacks, ChatMessagesCache } from "@/sdk";
import type { AgentSdkBase } from "@/sdk/agent/agentSdkBase";

import { isStreamsLogLevel } from "@/amazon-connect/streamsLogging";
import { env } from "@/config";
import { LogFlow } from "@/sdk/logging/flows";
import { userCache } from "@/shared-utils";

const logger = getLogger({ module: "createAgentSdk" });

/** Provider-agnostic parameters required to create the orchestrated agent SDK. */
export interface CreateSdkParams {
    /** Function to create HTML containers for provider UIs (e.g., Amazon Connect CCP) */
    createContainer: (name: string) => Promise<HTMLElement>;
    employeeNum: string | null;
    serviceProvider: ServiceProvider;
    userFriendlyName?: string;
    webSocket: ExpertWorkspaceWebSocket;
    /** Cache for storing and retrieving chat messages across providers */
    chatMessagesCache: ChatMessagesCache;
    /** Universal chat event callbacks - handles notifications, analytics, etc. */
    chatEventCallbacks: ChatEventCallbacks;
    onAuthRedirect?: VoidFunction;
}

/**
 * Factory functions for creating provider-specific agent SDKs
 */
type AgentSdkFactory = {
    [key in ServiceProvider]: (createSdkParams: CreateSdkParams) => Promise<AgentSdkBase>;
};

const agentSdkFactory: AgentSdkFactory = {
    AMAZON_CONNECT: async function create({
        createContainer,
        employeeNum,
        onAuthRedirect,
        chatEventCallbacks,
        chatMessagesCache,
    }: CreateSdkParams) {
        const { amazonConnectInstancesInfo, allowedPartners, getPartnersConfig } = getAppConfigSingleton();
        if (!amazonConnectInstancesInfo) throw new Error("amazonConnectInstancesInfo is not defined");

        // TODO: allow multi-partner configuration.
        const partner = selectPartnerWithOverride(allowedPartners[0]);
        const staticConfigKey = getPartnersConfig(partner)?.amazonConnectInstancesInfoConfigKey;
        const partnerWithOrg = userCache.org ? `${partner}:${userCache.org}` : partner;

        const context = {
            environment: import.meta.env.VITE_ENV_TYPE as string,
            channel: env.channel || "voice",
            employeeId: employeeNum ?? "",
            appName: "expert-workspace",
            source: "expert-ui",
            partner,
        };
        const [
            configKeyOverride,
            enableGlobalResiliency,
            allowFramedSoftphone,
            vdiPlatform,
            autoAcceptMindfulCallbacks,
            streamsLogLevel,
        ] = await Promise.all([
            getFeature<string | null>("EWP-amazonConnectInstancesInfoConfigKeyOverride", context),
            getFeature<boolean>("EWP-isAmazonConnectGlobalResiliencyEnabled", context),
            getFeature<boolean>("EWP-isAmazonConnectAllowFramedSoftphoneEnabled", context),
            getFeature<string | null>("EWP-amazonConnectVirtualDesktopInfrastructurePlatform", context),
            getFeature<boolean>("EWP-autoAcceptMindfulCallbacks", context),
            getFeature<string>("EWP-amazonConnectStreamsLogLevel", context).then((value) => {
                if (isStreamsLogLevel(value)) return value;
                if (value) logger.warn({ value }, "Ignoring unrecognised amazon-connect-streams log level");
                return "WARN";
            }),
        ]);

        // A feature flag may override the resolved catalog key (used to canary Connect instance
        // migrations per-expert); fall back to the partner's static key when unset or when the
        // override names a key not present in the catalog.
        const amazonConnectInstancesInfoConfigKey =
            configKeyOverride && configKeyOverride in amazonConnectInstancesInfo ? configKeyOverride : staticConfigKey;

        let connectInstanceInfo = amazonConnectInstancesInfo["others"];
        if (amazonConnectInstancesInfoConfigKey && amazonConnectInstancesInfoConfigKey in amazonConnectInstancesInfo) {
            connectInstanceInfo = amazonConnectInstancesInfo[amazonConnectInstancesInfoConfigKey];
        } else if (partnerWithOrg in amazonConnectInstancesInfo) {
            connectInstanceInfo = amazonConnectInstancesInfo[partnerWithOrg];
        }

        const { instanceAlias, instanceId, region, identityProvider, secondaryInstanceAlias } = connectInstanceInfo;

        const ccpUrl = `https://${instanceAlias}.my.connect.aws/connect/ccp-v2`;
        const secondaryCCPUrl = secondaryInstanceAlias
            ? `https://${secondaryInstanceAlias}.my.connect.aws/connect/ccp-v2`
            : undefined;

        const { default: AmazonConnectAgentSDK } = await import("../amazon-connect/AmazonConnectAgentSDK");

        const container = await createContainer(`Amazon Connect Contact Control Panel: ${instanceAlias}`);

        const { loginPopup: loginPopupFromConfig, loginPopupAutoClose } = await getAmazonConnectLoginPopupConfig(
            "ExWo",
            context,
        );
        const isHttps = globalThis.location.protocol === "https:";
        const loginPopup = loginPopupFromConfig || !isHttps;
        const destinationRelay = loginPopup ? undefined : globalThis.location.href;
        const loginUrl =
            identityProvider && getAmazonConnectLoginUrl(identityProvider, region, instanceId, { destinationRelay });

        setFullStoryUserProperties({
            connectInstanceAlias: instanceAlias,
            partner,
            isAmazonConnectAllowFramedSoftphoneEnabled: allowFramedSoftphone,
            isAmazonConnectGlobalResiliencyEnabled: enableGlobalResiliency,
            amazonConnectLoginMethod: loginPopup ? "popup" : "redirect",
            amazonConnectVirtualDesktopInfrastructurePlatform: vdiPlatform,
            autoAcceptMindfulCallbacks,
            amazonConnectStreamsLogLevel: streamsLogLevel,
        });

        logger.info(
            {
                flow: LogFlow.WorkspaceLoading,
                partner,
                instanceAlias,
                ccpUrl,
                secondaryCCPUrl,
                enableGlobalResiliency,
                allowFramedSoftphone,
            },
            "Creating Amazon Connect agent SDK",
        );

        const result = await AmazonConnectAgentSDK.create(container, ccpUrl, partner, employeeNum, {
            secondaryCCPUrl,
            enableGlobalResiliency,
            autoAcceptMindfulCallbacks,
            allowFramedSoftphone,
            vdiPlatform: vdiPlatform ?? undefined,
            loginUrl,
            loginPopup,
            loginPopupAutoClose,
            metricsHandler: sendMetrics,
            onAuthRedirect,
            connectRegion: region,
            streamsLogLevel,
            // Only pass chat dependencies when chat channel is enabled
            ...(env.channel === "chat"
                ? {
                      chatDependencies: { chatEventCallbacks, chatMessagesCache },
                  }
                : {}),
        });

        return result;
    },
    EXPERT_UNIVERSITY: async () => {
        if (env.channel === "chat") {
            throw new Error("EXPERT_UNIVERSITY is not available for chat channel");
        }

        const { default: ExpertUniversitySdk } = await import("../expert-university/ExpertUniversitySdk");
        return new ExpertUniversitySdk();
    },
    MOCK: async ({ employeeNum }: CreateSdkParams) => {
        // Defense in depth: never construct the mock provider in a production build.
        if (env.isProdMode) {
            throw new Error("MOCK service provider is not available in production");
        }

        const { MockVoiceAgentSdk } = await import("../mock-sdk/MockVoiceAgentSdk");

        const { allowedPartners } = getAppConfigSingleton();
        const partner = selectPartnerWithOverride(allowedPartners[0]);
        const sdk = new MockVoiceAgentSdk(partner);
        sdk.initialize(employeeNum ?? "mock-agent");
        return sdk;
    },
};

/**
 * Initializes all specified service providers and combines them
 * into a single orchestrated SDK instance that supports multiple providers.
 */
export async function createAgentSDK(params: CreateSdkParams) {
    return await agentSdkFactory[params.serviceProvider](params);
}
